import type { FieldConfig, SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Button } from '@pesto/ui/components/Button';
import { DropdownMenuComponent } from '@pesto/ui/components/DropdownMenu';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { toast } from '@pesto/ui/components/Toaster';
import { Typography } from '@pesto/ui/components/Typography';
import { SmartForm } from '@pesto/ui/features/SmartForm/SmartForm';
import { SmartFormField } from '@pesto/ui/features/SmartForm/SmartFormField';
import { ChevronDownIcon, RocketIcon } from 'lucide-react';

import type { SampleVariant } from '../../../pages/Users/hooks/useRandomUserGenerator';
import type { LoanModalPayload, ModalProps } from '../../types/modal.types';
import type { OpenAccountFormData } from './openAccountSchema';
import type { SegmentOption } from './SegmentedField';

import {
  CardPlan,
  KycSource,
  useListProgramsQuery,
  useOpenAccountMutationMutation,
} from '../../../__generated__/graphql/api';
import { IS_PROD } from '../../../constants/global-env';
import { useRandomUserGenerator } from '../../../pages/Users/hooks/useRandomUserGenerator';
import { getOnboardMutationRefreshOptions } from '../../../utils/userCacheInvalidation';
import { ApplicantsSection } from './ApplicantsSection';
import { describeOpening } from './openAccountOutcome';
import {
  emptyApplicant,
  isAttestedImport,
  MAX_APPLICANTS,
  openAccountSchema,
  toOpenAccountVariables,
} from './openAccountSchema';
import { summarizeOpening } from './openAccountSummary';
import { OpenAccountSummaryRail } from './OpenAccountSummaryRail';
import { SegmentedField } from './SegmentedField';

const TEST_ATTESTATION_REASON = 'Test loan opened outside production';

const OUTCOME_TOAST_DURATION_MS = 10000;

type OpenAccountField = FieldConfig<OpenAccountFormData>;

const PROGRAM_FIELD: OpenAccountField = {
  name: 'program_id',
  label: 'Program',
  type: 'select',
  options: [],
  required: true,
};

const PARTNER_ACCOUNT_NUMBER_FIELD: OpenAccountField = {
  name: 'partner_account_number',
  label: 'Partner Account Number',
  type: 'text',
  required: true,
};

const KYC_OPTIONS: Array<SegmentOption<KycSource>> = [
  { label: 'Run KYC', value: KycSource.Screen },
  { label: 'Skip KYC', value: KycSource.Attest, selectedClassName: 'bg-warning text-warning-foreground' },
];

const CARD_PLAN_OPTIONS: Array<SegmentOption<CardPlan>> = [
  { label: 'Issue Lithic cards', value: CardPlan.Issue },
  { label: 'Import i2c cards', value: CardPlan.Import },
];

const ATTESTATION_REASON_FIELD: OpenAccountField = {
  name: 'attestation_reason',
  label: 'Reason for Skipping KYC',
  type: 'text',
  placeholder: 'e.g. Identity verified by the partner',
  required: true,
};

const TERMS_FIELDS: OpenAccountField[] = [
  {
    name: 'credit_limit',
    label: 'Credit Limit',
    component: CurrencyField,
    componentProps: { showCurrencySymbol: true },
    required: true,
  },
  { name: 'initial_apr', label: 'Initial APR', type: 'text', placeholder: 'e.g. 8.5', required: true },
  { name: 'apr_margin', label: 'APR Margin', type: 'text', placeholder: 'e.g. 2.5', required: true },
  { name: 'origination_date', label: 'Origination Date', type: 'date', required: true },
  {
    name: 'initial_balance',
    label: 'Opening Balance',
    component: CurrencyField,
    componentProps: { showCurrencySymbol: true },
    helperText: 'Optional starting balance.',
  },
  { name: 'promo_end_date', label: 'Promo End Date', type: 'date', helperText: 'Optional 0% promo end date.' },
];

const formConfig: SmartFormConfig<OpenAccountFormData> = {
  schema: openAccountSchema,
  defaultValues: {
    applicants: [emptyApplicant],
    program_id: '',
    partner_account_number: '',
    kyc: IS_PROD ? undefined : KycSource.Attest,
    attestation_reason: IS_PROD ? '' : TEST_ATTESTATION_REASON,
    card_plan: IS_PROD ? undefined : CardPlan.Import,
    credit_limit: 0,
    initial_balance: 0,
    origination_date: '',
    promo_end_date: '',
    apr_margin: '',
    initial_apr: '',
  },
};

export const OpenAccountModalComponent = ({ api }: ModalProps<undefined, LoanModalPayload | undefined>) => {
  const { handleMutation } = useMutationWithToast();
  const [openAccount, { loading }] = useOpenAccountMutationMutation(getOnboardMutationRefreshOptions());

  const { data: programsData, loading: programsLoading } = useListProgramsQuery({
    variables: { active: true, fullResults: true, paginationDetailsOnly: false },
    fetchPolicy: 'cache-first',
  });

  const programOptions = (programsData?.listPrograms?.programs ?? [])
    .filter((p) => p?.active)
    .map((p) => ({ label: p.name ?? p.id, value: p.id }));

  const handleSubmit = async (data: OpenAccountFormData) => {
    await handleMutation({
      mutation: () => openAccount({ variables: toOpenAccountVariables(data) }),
      extractSuccess: (result) => !!result?.data?.openAccount?.accountId,
      onSuccess: (result) => {
        const opening = result.data?.openAccount;

        if (!opening) return;

        const { tone, title, description } = describeOpening(opening);
        toast[tone](title, { description, duration: OUTCOME_TOAST_DURATION_MS });
        api.close(opening.loanId ? { loanId: opening.loanId } : undefined);
      },
      debugLabel: 'open-account-modal',
      toast: {
        error: 'Failed to open account',
        loading: 'Opening account...',
      },
    });
  };

  const { generateSampleApplicant, generateSampleLoanTerms } = useRandomUserGenerator();

  return (
    <SmartForm
      config={formConfig}
      onSubmit={handleSubmit}
      loading={loading || programsLoading}
      bottomContent={() => null}
    >
      {(form) => {
        const defaultProgramId = programOptions[0]?.value;

        if (!programsLoading && defaultProgramId && !form.getValues('program_id')) {
          form.setValue('program_id', defaultProgramId);
        }

        const [kyc, cardPlan] = form.watch(['kyc', 'card_plan']);
        const revalidate = { shouldDirty: true, shouldValidate: form.formState.isSubmitted };
        const errorOf = (name: OpenAccountField['name']) => form.getFieldState(name, form.formState).error;

        const renderField = (field: OpenAccountField) => (
          <SmartFormField
            key={field.name}
            field={field}
            value={form.watch(field.name)}
            onChange={(value) => form.setValue(field.name, value, revalidate)}
            error={errorOf(field.name)}
          />
        );

        const handlePrepopulate = (variant: SampleVariant) => {
          const terms = generateSampleLoanTerms(variant);

          form.setValue(
            'applicants',
            form.getValues('applicants').map(() => generateSampleApplicant()),
          );
          form.setValue('credit_limit', terms.credit_limit);
          form.setValue('partner_account_number', terms.partner_account_number);
          form.setValue('origination_date', terms.origination_date);
          form.setValue('initial_balance', terms.initial_balance);
          form.setValue('promo_end_date', terms.promo_end_date);
          form.setValue('apr_margin', terms.apr_margin);
          form.setValue('initial_apr', terms.initial_apr);

          if (!form.getValues('program_id') && defaultProgramId) {
            form.setValue('program_id', defaultProgramId);
          }
        };

        const rows = summarizeOpening({
          ...form.watch(),
          programName: programOptions.find((option) => option.value === form.watch('program_id'))?.label,
        });

        return (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
            <div className="flex min-w-0 flex-col gap-8">
              <section className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-2">
                  <Typography variant="headerSmall">Account</Typography>
                  {!IS_PROD && (
                    <DropdownMenuComponent
                      trigger={
                        <Button variant="outline" size="sm">
                          <RocketIcon className="h-4 w-4" />
                          Sample Data
                          <ChevronDownIcon className="h-3.5 w-3.5" />
                        </Button>
                      }
                      content={[
                        { label: 'Standard', onClick: () => handlePrepopulate('standard') },
                        { label: 'With Promo', onClick: () => handlePrepopulate('promo') },
                        {
                          label: 'With Promo and Opening Balance',
                          onClick: () => handlePrepopulate('promoOpeningBalance'),
                        },
                      ]}
                    />
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {renderField({ ...PROGRAM_FIELD, options: programOptions })}
                  {renderField(PARTNER_ACCOUNT_NUMBER_FIELD)}
                  <SegmentedField
                    label="KYC"
                    options={KYC_OPTIONS}
                    value={kyc}
                    onChange={(value) => form.setValue('kyc', value, revalidate)}
                    error={errorOf('kyc')?.message}
                    required
                  />
                  <SegmentedField
                    label="Cards"
                    options={CARD_PLAN_OPTIONS}
                    value={cardPlan}
                    onChange={(value) => form.setValue('card_plan', value, revalidate)}
                    error={errorOf('card_plan')?.message}
                    required
                  />
                  {kyc === KycSource.Attest && (
                    <div className="sm:col-span-2">{renderField(ATTESTATION_REASON_FIELD)}</div>
                  )}
                </div>
              </section>

              <ApplicantsSection
                control={form.control}
                maxApplicants={MAX_APPLICANTS}
                hideCardFields={cardPlan !== CardPlan.Import}
                ssnOptional={isAttestedImport({ kyc, card_plan: cardPlan })}
              />

              <section className="flex flex-col gap-4">
                <Typography variant="headerSmall">Loan Terms</Typography>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">{TERMS_FIELDS.map(renderField)}</div>
              </section>
            </div>

            <OpenAccountSummaryRail rows={rows}>
              <Button type="submit" loading={loading} disabled={loading || programsLoading} className="w-full">
                Open Account
              </Button>
            </OpenAccountSummaryRail>
          </div>
        );
      }}
    </SmartForm>
  );
};
